export interface ChatBootstrapLoaders<TSessions, TQuota, TModels> {
  loadSessions: () => Promise<TSessions>;
  loadQuota: () => Promise<TQuota>;
  loadModels: () => Promise<TModels>;
  onSessions: (sessions: TSessions) => void | Promise<void>;
  onMetadata: (metadata: { quota: TQuota | null; models: TModels | null }) => void | Promise<void>;
}

/**
 * The conversation list is independently useful and must not wait for model
 * discovery, quota decoration, or the selected transcript to become usable.
 */
export async function loadChatSidebarFirst<TSessions, TQuota, TModels>({
  loadSessions,
  loadQuota,
  loadModels,
  onSessions,
  onMetadata
}: ChatBootstrapLoaders<TSessions, TQuota, TModels>): Promise<void> {
  const sessionsPromise = loadSessions();
  const quotaPromise = loadQuota();
  const modelsPromise = loadModels();

  const sessions = await sessionsPromise;
  await onSessions(sessions);

  // The sidebar is the first useful surface. Do not keep page bootstrap open
  // while unrelated quota/model metadata is still loading; those values can
  // decorate the already-rendered sidebar when they arrive.
  void Promise.allSettled([quotaPromise, modelsPromise])
    .then(async ([quota, models]) => {
      await onMetadata({
        quota: quota.status === "fulfilled" ? quota.value : null,
        models: models.status === "fulfilled" ? models.value : null
      });
    })
    .catch((error) => {
      console.error("loadChatMetadata failed:", error);
    });
}
